<template>
  <section class="user-bonuses" :class="platform">
    <DepositChainPrograms
      v-if="preparedBonusesChain"
      :chain="preparedBonusesChain"
      class="chain-programs"
    />
    <div class="regular-bonuses" :class="{ empty: !hasRegularBonuses }">
      <h3 class="bonuses-title">{{ t('bonuses.regularBonuses') }}</h3>
      <div v-if="hasRegularBonuses" class="bonuses-grid">
        <RegularBonusCard
          v-for="bonus in regularBonusesItems"
          ref="regularBonus"
          :key="bonus.id"
          :bonus="bonus"
          :highlight="!isLoading ? highlightedBonuses : null"
        />
        <DepositBonusCard
          v-for="depositBonus in allDepositPrograms"
          ref="depositBonus"
          :key="depositBonus.id"
          :bonus="depositBonus"
          :highlight="isHighlighting('deposit')"
        />
        <FreeSpinCard
          v-for="freespin in depositFreespins"
          ref="freespin"
          :key="freespin.userFreespinProgramId"
          :freespin="freespin"
          :highlight="isHighlighting('freespin')"
        />
        <FreeBetCard
          v-for="freebet in depositFreebets"
          ref="freebet"
          :key="freebet.id"
          :freebet="freebet"
          :highlight="isHighlighting('freebet')"
        />
        <!-- Deposit bonuses end -->
        <FreeSpinCard
          v-for="freespin in freespins"
          ref="freespin"
          :key="freespin.userFreespinProgramId"
          :freespin="freespin"
          :highlight="isHighlighting('freespin')"
        />
        <FreeBetCard
          v-for="freebet in freebets"
          ref="freebet"
          :key="freebet.id"
          :freebet="freebet"
          :highlight="isHighlighting('freebet')"
        />
        <CashBonusCard
          v-for="cashBonus in depositCashBonuses"
          ref="cashbonus"
          :key="cashBonus.id"
          :bonus="cashBonus"
          :highlight="isHighlighting('cashbonus')"
        />
      </div>
      <div v-else class="bonuses-empty">
        <StIcon
          name="gift"
          :size="isMobile ? 24 : 32"
          color="var(--text-tertiary)"
        />
        <span class="empty-text">
          {{ t('bonuses.emptyRegulatBonusesText') }}
        </span>
        <StButton
          class="button"
          type="text-only"
          to="/bonus-info"
          :size="isMobile ? 'm' : 'xl'"
        >
          {{ t('bonuses.exploreTheBonusSystem') }}
        </StButton>
      </div>
    </div>
    <div
      class="vip-bonuses"
      :class="{ empty: !hasVipBonuses, mobile: isMobile }"
    >
      <h3 class="bonuses-title">{{ t('bonuses.vipBonuses') }}</h3>
      <div v-if="hasVipBonuses" class="bonuses-grid">
        <RegularBonusCard
          v-for="bonus in vipBonuses"
          ref="regularBonus"
          :key="bonus.id"
          :bonus="bonus"
          :highlight="!isLoading ? highlightedBonuses : null"
        />
      </div>
      <div v-else class="bonuses-empty" :class="platform">
        <StIcon
          name="crown"
          :size="isMobile ? 24 : 32"
          color="var(--text-tertiary)"
        />
        <span class="empty-text">{{ t('bonuses.emptyBonusesText') }}</span>
        <StButton
          class="button"
          type="text-only"
          to="/vip-club"
          :size="isMobile ? 'm' : 'xl'"
          >{{ t('bonuses.howToGet') }}</StButton
        >
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import DepositChainPrograms from '../DepositChainPrograms/DepositChainPrograms.vue'
import { useDepositBonusesChain } from '../../composables/useDepositBonusesChain'
import { useFreeBonuses } from '../../composables/useFreeBonuses'
import { useDepositBonusesStore } from '../../stores/useDepositBonusesStore'
import { useRegularBonusesStore } from '../../stores/useRegularBonusesStore'
import DepositBonusCard from '../DepositBonusCard/DepositBonusCard.vue'
import CashBonusCard from '../CashBonus/CashBonusCard.vue'
import FreeSpinCard from '../FreeBonusCard/FreeSpinCard/FreeSpinCard.vue'
import FreeBetCard from '../FreeBonusCard/FreeBetCard/FreeBetCard.vue'
import RegularBonusCard from '../RegularBonusCard/RegularBonusCard.vue'
import { useHighlightBonuses } from './useHighlightBonuses'

const { platform, isMobile } = usePlatform()
const { t } = useI18n()

const { preparedBonusesChain } = useDepositBonusesChain()
const {
  allDepositPrograms,
  depositCashBonuses,
  depositFreespins,
  depositFreebets,
} = storeToRefs(useDepositBonusesStore())

const { freebets, freespins } = useFreeBonuses()

const regularBonusesStore = useRegularBonusesStore()
const { regularBonuses: regularBonusesItems, vipBonuses } =
  storeToRefs(regularBonusesStore)

const { highlightedBonuses } = useHighlightBonuses()

const depositBonuses = useTemplateRef('depositBonus')
const cashBonuses = useTemplateRef('cashbonus')
const freespinBonuses = useTemplateRef('freespin')
const freebetBonuses = useTemplateRef('freebet')
const regularBonuses = useTemplateRef('regularBonus')

const activeBonus = computed(() => {
  if (!highlightedBonuses.value) return null
  const bonuses = {
    deposit: depositBonuses.value,
    cashbonus: cashBonuses.value,
    freespin: freespinBonuses.value,
    freebet: freebetBonuses.value,
    reload: regularBonuses.value,
    cashback: regularBonuses.value,
    rakeBack: regularBonuses.value,
    tournament: regularBonuses.value,
  }[highlightedBonuses.value]

  return Array.isArray(bonuses) ? bonuses[0] : bonuses
})

const isLoading = ref(false)
function isHighlighting(bonusType: string) {
  return highlightedBonuses.value === bonusType && !isLoading.value
}
onMounted(async () => {
  if (activeBonus.value?.$el) {
    activeBonus.value.$el.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }
})

const hasRegularBonuses = computed(
  () =>
    !!(
      allDepositPrograms.value.length ||
      depositFreespins.value.length ||
      depositFreebets.value.length ||
      freespins.value.length ||
      freebets.value.length ||
      depositCashBonuses.value.length ||
      regularBonusesItems.value.length
    ),
)

const hasVipBonuses = computed(() => vipBonuses.value.length > 0)
</script>

<style scoped>
.chain-programs {
  order: -1;
}

.bonuses-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-150);
  margin: calc(var(--spacing-025) * -1) calc(var(--spacing-025) * -1);
}

.user-bonuses {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-200);
  width: 100%;

  &.mobile {
    .bonuses-grid {
      grid-template-columns: 1fr;
      gap: var(--spacing-050);
    }
  }
}

.bonuses-title {
  margin: 0;
  margin-bottom: var(--spacing-150);
  font: var(--desktop-text-lg-semibold);
}

.vip-bonuses {
  order: -1;

  &.empty {
    order: 0;
  }
}

.bonuses-empty {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-150);
  align-items: center;

  width: 100%;
  padding: var(--spacing-300);

  border: var(--border-width-medium, 1px) solid
    var(--border-primary, rgb(255 255 255 / 12%));
  border-radius: var(--border-radius-150, 12px);

  .empty-text {
    font: var(--desktop-text-md-medium);
    color: var(--text-tertiary);
    text-align: center;
  }

  &.mobile {
    gap: var(--spacing-125);
    padding: var(--spacing-250) var(--spacing-200);

    .empty-text {
      font: var(--mobile-text-content-regular);
    }
  }
}
</style>
